import { create } from 'zustand';
import * as PaymentService from '@helper/service/payment';

const ACTIONED_CASE_DECISIONS = ['ACPT', 'RJCT', 'FINO'];
const PAYTO_QUERY_PAYMENTS = 'Mandate Query Payments';
const PAYTO_QUERY_CASE = 'Mandate Query Case';

export const canAccessPaytoQueryForRoles = (query, roles = []) => {
  const roleList = Array.isArray(roles) ? roles : [];
  const canRefund = roleList.includes('payto-refunder');
  const canRespondToQueries = roleList.includes('support');
  const queryType = query?.query || query?.case_type;

  if (queryType === PAYTO_QUERY_PAYMENTS) {
    return canRefund;
  }

  if (queryType === PAYTO_QUERY_CASE) {
    return canRespondToQueries;
  }

  return canRefund && canRespondToQueries;
};

const isActionedPaytoQuery = (query) =>
  ACTIONED_CASE_DECISIONS.includes(
    String(query?.case_decision || query?.caseDecision || '').toUpperCase(),
  );

const getMandateIdentification = (query) =>
  query?.mandateId ||
  query?.mandate_details?.mandate_identification ||
  query?.mandate_id ||
  query?.mandateIdentification ||
  query?.mandate_identification;

const mapPaytoQueryToTableRow = (item, index) => {
  const rawMandateId = getMandateIdentification(item);
  const mandateId = rawMandateId || 'N/A';
  const caseDecision = item?.case_decision || item?.caseDecision || '';

  return {
    id: item?._id || item?.request_id || rawMandateId || String(index + 1),
    _id: item?._id,
    request_id: item?.request_id,
    mandateId,
    case_id: item?.case_id || 'N/A',
    query: item?.case_type || 'N/A',
    amount:
      item?.amount ||
      item?.payment_details?.original_transaction_amount?.amount ||
      item?.payment_information?.amount?.amount ||
      item?.mandate_details?.payment_information?.amount?.amount ||
      'N/A',
    queryDate:
      item?.created_at ||
      item?.created_At ||
      item?.createdAt ||
      'N/A',
    healthEntity: item?.healthEntityCode || 'N/A',
    receivableActivityDisplay: item?.receivableActivityDisplay || 'N/A',
    hospitalFacility: item?.hospitalFacility || 'N/A',
    narrativeQuery: item?.narrativeQuery || 'N/A',
    narrative: item?.narrative,
    endToEndId: item?.endToEndId,
    caseDecision: caseDecision || 'N/A',
    case_decision: caseDecision,
    status: item?.status,
    resolution: item?.resolution,
    resolvedAt: item?.resolvedAt,
    isActioned: isActionedPaytoQuery(item),
    rawQuery: item,
  };
};

const buildPaytoQueryState = (queries) => {
  const queryList = Array.isArray(queries) ? queries : [];
  const tableData = queryList.map(mapPaytoQueryToTableRow);

  return {
    queries: queryList,
    tableData,
    actionedQueries: tableData.filter((query) => query.isActioned),
    unactionedQueries: tableData.filter((query) => !query.isActioned),
  };
};

const getQueriesFromResponse = (response) => {
  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response)) {
    return response;
  }

  if (response?.success === true && response.data == null) {
    return [];
  }

  return null;
};

const getQueryId = (query) => query?._id || query?.request_id || query?.id;

const includesSearchTerm = (value, searchTerm) =>
  String(value || '').toLowerCase().includes(searchTerm);

const isSamePaytoQuery = (query, selectedQuery) => {
  const queryId = getQueryId(query);
  const selectedQueryId = getQueryId(selectedQuery);

  if (queryId && selectedQueryId && queryId === selectedQueryId) {
    return true;
  }

  const queryMandateId = getMandateIdentification(query);
  const selectedMandateId = getMandateIdentification(selectedQuery);

  return Boolean(
    query?.case_id &&
      selectedQuery?.case_id &&
      queryMandateId &&
      selectedMandateId &&
      query.case_id === selectedQuery.case_id &&
      queryMandateId === selectedMandateId,
  );
};

export const usePaytoQueryStore = create((set, get) => ({
  // State
  queries: [],
  tableData: [],
  actionedQueries: [],
  unactionedQueries: [],
  isLoading: false,
  error: null,
  selectedQuery: null,
  filters: {
    searchTerm: '',
    status: null,
  },

  // Actions
  /**
   * Creates a PayTo query and adds it to the store
   * @param {object} mandate_details - The mandate details
   * @param {string} type - Query type: 'mandate' or 'payment'
   * @param {function} callback - Optional callback function
   * @returns {Promise<object>} - Response from API
   */
  addQuery: async (mandate_details, type = 'mandate', callback) => {
    set({ isLoading: true, error: null });

    // Build base payload fields
    const caseIdDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const caseIdRandom = Math.floor(10000 + Math.random() * 90000);
    const basePayload = {
      case_id: `ANZAU${caseIdDate}-${caseIdRandom}`,
      investigation_type_code: 'INV14',
      // healthEntityCode: mandate_details?.healthEntityCode || null,
      // hospitalFacility: mandate_details?.hospitalFacility || null,
      // receivableActivityDisplay: mandate_details?.receivableActivityDisplay || null,
    };

    // Normalize incoming mandate details
    const md = mandate_details || {};

    // remove currency from paymentInformation
    const paymentInformation =
        md.payment_information ?? md.paymentInformation ?? null;

    let sanitizedPaymentInformation = null;
    if (paymentInformation) {
      const { currency: _removedCurrency, ...amountWithoutCurrency } =
        paymentInformation.amount ?? {};

      sanitizedPaymentInformation = {
        ...paymentInformation,
        amount: amountWithoutCurrency,
      };
    }

    // Prepare mandate_details object for payload using available fields
    const payloadMandateDetails = {
      mps_user_id: process.env.REACT_APP_MPS_USER_ID,
      mandate_identification: md.mandate_identification || md.mandateIdentification || md._id || null,
      establishment_scheme: md.establishment_scheme || md.establishmentScheme || md.establishment || null,
      mandate_purpose_code: md.mandate_purpose_code || md.mandate_purpose || md.mandate_purpose_code || null,
      description: md.description || md.additional_information || null,
      debtor_information: md.debtor_information
        ? {
            party_name: md.debtor_information.party_name || md.debtor_information.partyName || null,
            // account_identification_type_code:
            //   md.debtor_information.account_identification_type_code || md.debtor_information.account_identification_type || md.debtor_information.account_identification_type_code || null,
            // account_identification:
            //   md.debtor_information.account_identification || md.debtor_information.account_alias_identification || md.debtor_information.account_alias_identification || null,
            account_alias_type_code: md.debtor_information.account_alias_type_code || md.debtor_information.account_alias_type || null,
            account_alias_identification: md.debtor_information.account_alias_identification || null,

          // [md.debtor_information.account_identification ? 'account_identification' : 'account_alias_identification']: 
          //   md.debtor_information.account_identification || md.debtor_information.account_alias_identification || null,

          }
        : null,
      creditor_information: md.creditor_information
        ? {
            party_reference: md.creditor_information.party_reference || md.creditor_information.partyReference || null,
            // party_name: md.creditor_information.party_name || null,
          }
        : null,
      // payment_information: md.payment_information || md.paymentInformation || null,
       payment_information : sanitizedPaymentInformation ,
      additional_information: md.additional_information || null,
    };

    let payload;

    if (String(type).toLowerCase() === 'payment' || String(type).toLowerCase() === 'mandatepayment') {
      // Build payment_details from mpir_webhook_response if available
      const mpir = md.mpir_webhook_response || md.mpirWebhookResponse || {};
      const originalRef = mpir.original_transaction_reference || mpir.original_transaction_reference || {};

      const transaction_identification = mpir.clearing_system_reference || mpir.clearingSystemReference || '';
      const instruction_identification = mpir.instruction_identification || mpir.instructionIdentification || originalRef.original_instruction_identification || mpir.instruction_identification || '';
      const transaction_date = (mpir.acceptance_date_time || mpir.creation_date_time || '').split('T')[0] || '';
      const original_instructed_amount = (originalRef.instructed_amount || originalRef.instructedAmount || mpir.instructed_amount || {}) ;

      const payment_details = {
        transaction_identification,
        instruction_identification,
        transaction_date,
        original_transaction_amount: original_instructed_amount.amount
          ? {
              // currency: original_instructed_amount.currency || 'AUD', // not required for anz v2 gateway
              amount: (original_instructed_amount.amount || '').toString(),
            }
          : null,
      };

      const narrativeQuery = `${md.narrativeQuery || md.reason || ''}`;

      payload = {
        ...basePayload,
        case_type: 'Mandate Query Payments',
        narrativeQuery,
        payment_details,
        mandate_details: payloadMandateDetails,
        // mps_user_id: process.env.REACT_APP_MPS_USER_ID,
        // payment_information: payloadMandateDetails.payment_information,
      };
    } else {
      // Mandate Query Case
      const narrativeQuery = `${md.narrativeQuery || md.reason || ''}`;

      payload = {
        ...basePayload,
        case_type: 'Mandate Query Case',
        narrativeQuery,
        mandate_details: payloadMandateDetails,
        // mps_user_id: process.env.REACT_APP_MPS_USER_ID,
        // payment_information: payloadMandateDetails.payment_information,
      };
    }

    try {
      const response = await PaymentService.paytoCreateQueryNonProd(payload);

      set({ isLoading: false, error: null });

      // Add the created query to the store
      if (response?.data) {
        const newQueries = Array.isArray(response.data) ? response.data : [response.data];
        set((state) => ({
          ...buildPaytoQueryState([...newQueries, ...state.queries]),
        }));
      }

      if (callback) callback(response);
      return response;
    } catch (e) {
      const errorMessage = e?.message || String(e);
      set({ 
        error: errorMessage,
        isLoading: false 
      });
      console.error('Error creating PayTo query:', e);
      if (callback) callback(null);
      return null;
    }
  },

  /**
   * Fetches all queries from the backend via paytoRetrieveQuery
   * @returns {Promise<object>} - Response from paytoRetrieveQuery
   */
  fetchQueries: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await PaymentService.paytoRetrieveQuery();

      const queries = getQueriesFromResponse(response);
      if (!queries) {
        throw new Error('Invalid response format from paytoRetrieveQuery');
      }

      set({
        ...buildPaytoQueryState(queries),
        error: null,
      });

      return response;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch queries';
      set({
        error: errorMessage,
        ...buildPaytoQueryState([]),
      });
      console.error('Error fetching PayTo queries:', err);
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Adds a new query to the store (typically after creating one)
   * @param {object} query - The query object to add
   */
  addNewQuery: (query) => {
    set((state) => ({
      ...buildPaytoQueryState([query, ...state.queries]),
    }));
  },

  /**
   * Resolves a PayTo query with a case decision
   * @param {object} submitData - The form submission data (caseDecision, narrative, etc.)
   * @returns {Promise<object>} - Response from API
   */
  resolveQuery: async (submitData) => {
    set({ isLoading: true, error: null });

    try {
      // Find the query to get required data
      const query = get().selectedQuery;
      if (!query) {
        throw new Error('No selected query found');
      }
      console.log('Resolving query:', query);

      // Build the payload according to the required structure
      const payload = {
        mps_user_id: process.env.REACT_APP_MPS_USER_ID,
        request_id: query.request_id || query._id || crypto.randomUUID(),
        case_id: query.case_id,
        case_decision: submitData.caseDecision,
        mandate_identification: query.mandateId,
        reject_reason_code:
          submitData.reject_reason_code ||
          (submitData.caseDecision === 'RJCT' ? 'ARDT' : undefined),
        return_transaction_identification: submitData.return_transaction_identification,
        narrative: submitData.narrative
      };

      // Remove undefined fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const response = await PaymentService.paytoQueryResolution(payload, query.healthEntity);

      if (response?.success) {
        // Update the query in the store with resolved status
        set((state) => ({
          ...buildPaytoQueryState(
            state.queries.map((q) =>
              isSamePaytoQuery(q, query)
                ? {
                    ...q,
                    case_decision: payload.case_decision,
                    narrative: payload.narrative,
                    request_id: payload.request_id,
                    status: 'resolved',
                    resolution: payload,
                    resolvedAt: new Date().toISOString(),
                  }
                : q
            ),
          ),
          selectedQuery: null, // Clear selected query after resolution
          isLoading: false,
          error: null,
        }));

        return response;
      } else {
        throw new Error(response?.message || 'Failed to resolve query');
      }
    } catch (e) {
      const errorMessage = e?.message || String(e);
      set({ 
        error: errorMessage,
        isLoading: false 
      });
      console.error('Error resolving PayTo query:', e);
      return null;
    }
  },

  /**
   * Removes a query from the store
   * @param {string} queryId - The ID of the query to remove
   */
  removeQuery: (queryId) => {
    set((state) => ({
      ...buildPaytoQueryState(
        state.queries.filter((query) => getQueryId(query) !== queryId),
      ),
    }));
  },

  /**
   * Selects a query for detail view
   * @param {object|string} query - The query object or query ID
   */
  selectQuery: (query) => {
    if (typeof query === 'string') {
      const state = get();
      const selectedQuery =
        state.tableData.find(
          (q) => q._id === query || q.id === query || q.request_id === query,
        ) ||
        state.queries
          .map(mapPaytoQueryToTableRow)
          .find(
            (q) => q._id === query || q.id === query || q.request_id === query,
          );
      set({ selectedQuery });
    } else {
      set({ selectedQuery: query });
    }
  },

  /**
   * Clears the selected query
   */
  clearSelectedQuery: () => {
    set({ selectedQuery: null });
  },

  /**
   * Updates search filter
   * @param {string} searchTerm - The search term
   */
  setSearchFilter: (searchTerm) => {
    set((state) => ({
      filters: { ...state.filters, searchTerm },
    }));
  },

  /**
   * Updates status filter
   * @param {string|null} status - The status to filter by
   */
  setStatusFilter: (status) => {
    set((state) => ({
      filters: { ...state.filters, status },
    }));
  },

  /**
   * Resets all filters
   */
  resetFilters: () => {
    set({
      filters: {
        searchTerm: '',
        status: null,
      },
    });
  },

  /**
   * Clears all queries and state
   */
  clearQueries: () => {
    set({
      ...buildPaytoQueryState([]),
      selectedQuery: null,
      error: null,
    });
  },

  /**
   * Gets filtered queries based on current filters
   * @returns {array} - Filtered queries
   */
  getFilteredQueries: () => {
    const state = get();
    const { tableData, filters } = state;

    return tableData.filter((query) => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch =
          includesSearchTerm(query._id, searchLower) ||
          includesSearchTerm(query.id, searchLower) ||
          includesSearchTerm(query.case_id, searchLower) ||
          includesSearchTerm(query.mandateId, searchLower) ||
          includesSearchTerm(query.endToEndId, searchLower) ||
          includesSearchTerm(query.caseDecision, searchLower) ||
          includesSearchTerm(query.narrative, searchLower);

        if (!matchesSearch) return false;
      }

      if (filters.status === 'actioned' && !query.isActioned) {
        return false;
      }

      if (filters.status === 'unactioned' && query.isActioned) {
        return false;
      }

      if (
        filters.status &&
        !['actioned', 'unactioned'].includes(filters.status) &&
        query.status !== filters.status
      ) {
        return false;
      }

      return true;
    });
  },

  /**
   * Gets query statistics
   * @returns {object} - Statistics about the queries
   */
  getQueryStats: () => {
    const state = get();
    const queries = state.tableData;
    
    return {
      total: queries.length,
      actioned: state.actionedQueries.length,
      unactioned: state.unactionedQueries.length,
      byStatus: queries.reduce((acc, query) => {
        const status = query.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
      byCaseDecision: queries.reduce((acc, query) => {
        const caseDecision = query.case_decision || 'unactioned';
        acc[caseDecision] = (acc[caseDecision] || 0) + 1;
        return acc;
      }, {}),
    };
  },
}));
